import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Calendar, Loader2, RefreshCw, Plus, X, UserPlus, Trash2, ChevronRight, ChevronDown, ClipboardList } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  apiCall, type ClassSession, type ClassDef, type Coach, type ClassRegistration, type Member,
} from '@/lib/api';

function fmtDate(iso: string): { day: string; time: string } {
  const d = new Date(iso);
  return {
    day: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
}

function groupByDay(sessions: ClassSession[]): Array<{ day: string; sessions: ClassSession[] }> {
  const map = new Map<string, ClassSession[]>();
  for (const s of sessions) {
    const day = new Date(s.starts_at).toISOString().slice(0, 10);
    if (!map.has(day)) map.set(day, []);
    map.get(day)!.push(s);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, sessions]) => ({ day, sessions }));
}

export default function Schedule() {
  const { getToken } = useAuth();
  const [sessions, setSessions] = useState<ClassSession[]>([]);
  const [classes, setClasses] = useState<ClassDef[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [showAddSession, setShowAddSession] = useState(false);
  const [showAddClass, setShowAddClass] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const from = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const to = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      const [ss, cs, cos] = await Promise.all([
        apiCall<{ items: ClassSession[] }>('/v1/classes/sessions', { bearerToken: token, query: { from, to } }),
        apiCall<{ items: ClassDef[] }>('/v1/classes', { bearerToken: token }),
        apiCall<{ items: Coach[] }>('/v1/coaches', { bearerToken: token, query: { status: 'active' } }),
      ]);
      setSessions(ss.items);
      setClasses(cs.items);
      setCoaches(cos.items);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const groups = groupByDay(sessions);

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Schedule</h1>
          <p className="text-sm text-muted-foreground">
            Upcoming class sessions (next 14 days).
            {classes.length === 0 && ' Start by adding a class definition.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAddClass(true)}>
            <Plus className="mr-1 h-4 w-4" /> New class
          </Button>
          <Button size="sm" onClick={() => setShowAddSession(true)} disabled={classes.length === 0}>
            <Plus className="mr-1 h-4 w-4" /> Schedule session
          </Button>
        </div>
      </div>

      {err && <Card><CardContent className="py-4 text-sm text-destructive">{err}</CardContent></Card>}

      {!loading && groups.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <Calendar className="mx-auto mb-3 h-8 w-8 opacity-40" />
            Nothing on the schedule yet.
          </CardContent>
        </Card>
      )}

      {groups.map((g) => (
        <div key={g.day} className="space-y-2">
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground px-1">
            {new Date(g.day + 'T00:00:00Z').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </h2>
          <div className="space-y-2">
            {g.sessions.map((s) => {
              const t = fmtDate(s.starts_at);
              const isFull = s.registered_count >= s.capacity;
              const isOpen = expanded.has(s.id);
              return (
                <Card key={s.id}>
                  <CardContent className="p-0">
                    <div className="flex items-stretch">
                      <button
                        onClick={() => toggle(s.id)}
                        className="flex-1 flex items-center gap-4 px-4 py-3 hover:bg-muted/40 text-left min-w-0"
                      >
                        <div className="text-sm tabular-nums text-muted-foreground w-16 shrink-0">{t.time}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{s.class_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {s.coach_name ?? 'No coach'} · {s.registered_count}/{s.capacity} registered
                            {s.status !== 'scheduled' && <> · <Badge variant="outline">{s.status}</Badge></>}
                            {isFull && <Badge variant="destructive" className="ml-2">Full</Badge>}
                          </div>
                        </div>
                        {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      <Link
                        to={`/dashboard/schedule/${s.id}`}
                        title="Roll-call"
                        className="px-3 flex items-center border-l text-muted-foreground hover:text-foreground hover:bg-muted/40"
                      >
                        <ClipboardList className="h-4 w-4" />
                      </Link>
                    </div>
                    {isOpen && <SessionDetail session={s} onChange={load} />}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {showAddSession && (
        <AddSessionModal
          classes={classes} coaches={coaches}
          onClose={() => setShowAddSession(false)}
          onCreated={() => { setShowAddSession(false); void load(); }}
        />
      )}
      {showAddClass && (
        <AddClassModal
          coaches={coaches}
          onClose={() => setShowAddClass(false)}
          onCreated={() => { setShowAddClass(false); void load(); }}
        />
      )}
    </div>
  );
}

// ── Per-session expanded panel: roster + register ─────────────────────────
function SessionDetail({ session, onChange }: { session: ClassSession; onChange: () => void | Promise<void> }) {
  const { getToken } = useAuth();
  const [regs, setRegs] = useState<ClassRegistration[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [memberId, setMemberId] = useState('');
  const [walkInName, setWalkInName] = useState('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const token = await getToken();
        const [r, m] = await Promise.all([
          apiCall<{ items: ClassRegistration[] }>(`/v1/classes/sessions/${session.id}/registrations`, { bearerToken: token }),
          apiCall<{ items: Member[] }>('/v1/members', { bearerToken: token, query: { status: 'active' } }),
        ]);
        if (active) { setRegs(r.items); setMembers(m.items); }
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [session.id, getToken]);

  async function register() {
    setErr(null);
    try {
      const token = await getToken();
      const body: Record<string, string> = {};
      if (memberId) body.member_id = memberId;
      else if (walkInName.trim()) body.visitor_name = walkInName.trim();
      else return;
      await apiCall(`/v1/classes/sessions/${session.id}/registrations`, {
        method: 'POST', bearerToken: token, body,
      });
      setMemberId(''); setWalkInName('');
      const r = await apiCall<{ items: ClassRegistration[] }>(`/v1/classes/sessions/${session.id}/registrations`, { bearerToken: token });
      setRegs(r.items);
      void onChange();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
  }

  async function setRegStatus(id: string, status: ClassRegistration['status']) {
    const token = await getToken();
    await apiCall(`/v1/classes/registrations/${id}`, { method: 'PATCH', bearerToken: token, body: { status } });
    setRegs((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  }

  async function unregister(id: string) {
    const token = await getToken();
    await apiCall(`/v1/classes/registrations/${id}`, { method: 'DELETE', bearerToken: token });
    setRegs((prev) => prev.filter((r) => r.id !== id));
    void onChange();
  }

  const inp = 'rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  const registeredIds = new Set(regs.map((r) => r.member_id).filter((x): x is string => !!x));

  return (
    <div className="border-t px-4 py-3 bg-muted/20 space-y-3">
      {loading ? (
        <p className="text-xs text-muted-foreground">Loading roster…</p>
      ) : (
        <>
          {regs.length === 0 ? (
            <p className="text-xs text-muted-foreground">No one registered yet.</p>
          ) : (
            <ul className="space-y-1">
              {regs.map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate">{r.member_name ?? r.visitor_name ?? '(unknown)'}</span>
                  <select
                    value={r.status}
                    onChange={(e) => void setRegStatus(r.id, e.target.value as ClassRegistration['status'])}
                    className={inp + ' h-7 py-0 text-xs'}
                  >
                    <option value="registered">Registered</option>
                    <option value="attended">Attended</option>
                    <option value="no_show">No show</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => void unregister(r.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-end gap-2 pt-2 border-t">
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Register a member</label>
              <select
                value={memberId}
                onChange={(e) => { setMemberId(e.target.value); setWalkInName(''); }}
                className={inp + ' w-full'}
              >
                <option value="">— Pick a member —</option>
                {members.filter((m) => !registeredIds.has(m.id)).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="text-xs text-muted-foreground pb-1.5">or</div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Walk-in name</label>
              <input
                value={walkInName}
                onChange={(e) => { setWalkInName(e.target.value); setMemberId(''); }}
                placeholder="Guest name"
                className={inp + ' w-full'}
              />
            </div>
            <Button size="sm" onClick={() => void register()} disabled={!memberId && !walkInName.trim()}>
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Register
            </Button>
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
        </>
      )}
    </div>
  );
}

// ── Modals ──────────────────────────────────────────────────────────────
function AddSessionModal(props: {
  classes: ClassDef[]; coaches: Coach[];
  onClose: () => void; onCreated: () => void;
}) {
  const { getToken } = useAuth();
  const [classId, setClassId] = useState(props.classes[0]?.id ?? '');
  const [coachId, setCoachId] = useState('');
  const [startsAt, setStartsAt] = useState(() => {
    // default: today 6pm local
    const d = new Date(); d.setHours(18, 0, 0, 0);
    return d.toISOString().slice(0, 16); // local YYYY-MM-DDTHH:mm
  });
  const [capacity, setCapacity] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!classId || !startsAt) return;
    setSubmitting(true); setErr(null);
    try {
      const token = await getToken();
      const body: Record<string, unknown> = {
        class_id: classId,
        starts_at: new Date(startsAt).toISOString(),
      };
      if (coachId) body.coach_id = coachId;
      if (capacity) body.capacity = Number(capacity);
      await apiCall('/v1/classes/sessions', { method: 'POST', bearerToken: token, body });
      props.onCreated();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setSubmitting(false); }
  }

  const inp = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  return (
    <Modal title="Schedule a session" onClose={props.onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div><label className="block text-xs font-medium text-muted-foreground mb-1">Class *</label>
          <select value={classId} onChange={(e) => setClassId(e.target.value)} required className={inp}>
            {props.classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div><label className="block text-xs font-medium text-muted-foreground mb-1">When *</label>
          <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required className={inp} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">Coach (override)</label>
            <select value={coachId} onChange={(e) => setCoachId(e.target.value)} className={inp}>
              <option value="">— Use class default —</option>
              {props.coaches.map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
            </select>
          </div>
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">Capacity (override)</label>
            <input type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="—" className={inp} />
          </div>
        </div>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>Cancel</Button>
          <Button type="submit" disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Schedule
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AddClassModal(props: {
  coaches: Coach[];
  onClose: () => void; onCreated: () => void;
}) {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [duration, setDuration] = useState('60');
  const [capacity, setCapacity] = useState('20');
  const [coachId, setCoachId] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true); setErr(null);
    try {
      const token = await getToken();
      await apiCall('/v1/classes', {
        method: 'POST', bearerToken: token,
        body: {
          name: name.trim(),
          description: description.trim() || null,
          default_duration_min: Number(duration) || 60,
          default_capacity: Number(capacity) || 20,
          default_coach_id: coachId || null,
        },
      });
      props.onCreated();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setSubmitting(false); }
  }

  const inp = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  return (
    <Modal title="New class" onClose={props.onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div><label className="block text-xs font-medium text-muted-foreground mb-1">Class name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus className={inp} placeholder="e.g. Beginner BJJ" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">Duration (min)</label>
            <input type="number" min="1" value={duration} onChange={(e) => setDuration(e.target.value)} className={inp} />
          </div>
          <div><label className="block text-xs font-medium text-muted-foreground mb-1">Capacity</label>
            <input type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} className={inp} />
          </div>
        </div>
        <div><label className="block text-xs font-medium text-muted-foreground mb-1">Default coach</label>
          <select value={coachId} onChange={(e) => setCoachId(e.target.value)} className={inp}>
            <option value="">— None —</option>
            {props.coaches.map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
          </select>
        </div>
        <div><label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inp + ' resize-none'} />
        </div>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={props.onClose}>Cancel</Button>
          <Button type="submit" disabled={!name.trim() || submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-card text-card-foreground rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
